import type { ReactNode, SVGProps } from "react";

/**
 * A single stroked icon set at a consistent 24-unit grid and 1.8 stroke weight,
 * so icons sit together without one looking heavier than its neighbours.
 *
 * Icons are decorative by default (`aria-hidden`). When an icon is the only
 * content of a control, the control itself must carry the accessible name —
 * see IconButton.
 */
export type IconName =
  // navigation
  | "home"
  | "chat"
  | "work"
  | "insights"
  | "calendar"
  | "people"
  | "settings"
  | "document"
  | "checklist"
  // actions
  | "plus"
  | "search"
  | "filter"
  | "sort"
  | "close"
  | "check"
  | "more"
  | "send"
  | "attach"
  | "emoji"
  | "edit"
  | "trash"
  | "download"
  | "link"
  | "copy"
  | "forward"
  // direction
  | "chevron-left"
  | "chevron-right"
  | "chevron-down"
  | "chevron-up"
  | "arrow-right"
  // status and meta
  | "bell"
  | "clock"
  | "flag"
  | "trend-up"
  | "trend-down"
  | "info"
  | "warning"
  | "lock"
  | "eye"
  | "heart"
  | "reply"
  | "pin"
  | "mute"
  | "archive"
  | "repeat"
  | "restore"
  | "at"
  | "spinner";

const PATHS: Record<IconName, ReactNode> = {
  home: (
    <>
      <path d="m3 10 9-7 9 7" />
      <path d="M5 9v10h14V9" />
      <path d="M9 19v-6h6v6" />
    </>
  ),
  chat: <path d="M20 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />,
  work: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M8 6V4h8v2M3 12h18" />
    </>
  ),
  insights: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19a6 6 0 0 1 12 0M16.5 5.3a3.2 3.2 0 0 1 0 5.9M17.5 14a4.8 4.8 0 0 1 3.5 5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1-1.8 1.8-.1-.1a1.6 1.6 0 0 0-2.7 1.2v.2h-2.4v-.2a1.6 1.6 0 0 0-2.7-1.2l-.1.1-1.8-1.8.1-.1A1.6 1.6 0 0 0 4.6 15h-.2v-2.4h.2a1.6 1.6 0 0 0 1.2-2.7l-.1-.1 1.8-1.8.1.1A1.6 1.6 0 0 0 10.3 7v-.2h2.4V7a1.6 1.6 0 0 0 2.7 1.2l.1-.1 1.8 1.8-.1.1a1.6 1.6 0 0 0 1.2 2.7h.2V15z" />
    </>
  ),
  document: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </>
  ),
  checklist: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 1.5 1.5L11 8" />
      <path d="M13 9h5" />
      <path d="m7 15 1.5 1.5L11 14" />
      <path d="M13 15h5" />
    </>
  ),

  plus: <path d="M12 5v14M5 12h14" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  filter: <path d="M4 6h16M7 12h10M10 18h4" />,
  sort: <path d="M7 4v16l-3-3M17 20V4l3 3" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  check: <path d="M5 12l4 4L19 6" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </>
  ),
  send: <path d="M4 12h15M13 6l6 6-6 6" />,
  attach: <path d="m8 12 6.5-6.5a3 3 0 0 1 4.2 4.2L10 18.4a4 4 0 0 1-5.7-5.7l8-8" />,
  emoji: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    </>
  ),
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />,
  trash: <path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" />,
  download: <path d="M12 4v11M7 12l5 5 5-5M4 20h16" />,
  link: <path d="M9.5 14.5a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14.5 9.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </>
  ),
  // A share/redirect arrow, distinct from reply's backward curve — forward
  // sends a copy onward rather than answering in place.
  forward: (
    <>
      <path d="M14 6l6 6-6 6" />
      <path d="M4 12h16M4 12v-2a4 4 0 0 1 4-4h2" />
    </>
  ),

  "chevron-left": <path d="M15 5l-7 7 7 7" />,
  "chevron-right": <path d="M9 5l7 7-7 7" />,
  "chevron-down": <path d="M6 9l6 6 6-6" />,
  // The mirror of chevron-down, for reordering a list upward.
  "chevron-up": <path d="M18 15l-6-6-6 6" />,
  "arrow-right": <path d="M4 12h15M13 6l6 6-6 6" />,

  bell: <path d="M18 8A6 6 0 0 0 6 8c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  flag: <path d="M5 21V4h14l-3 5 3 5H5" />,
  "trend-up": (
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M17 7h4v4" />
    </>
  ),
  "trend-down": (
    <>
      <path d="M3 7l6 6 4-4 8 8" />
      <path d="M17 17h4v-4" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  heart: <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />,
  reply: (
    <>
      <path d="M9 7 4 12l5 5" />
      <path d="M4 12h11a5 5 0 0 1 5 5v1" />
    </>
  ),
  pin: <path d="M12 17v5M9 3h6l-1 6 3 3v2H7v-2l3-3z" />,
  mute: <path d="M18 8A6 6 0 0 0 6 8c0 7-3 7-3 9h18c0-2-3-2-3-9M2 2l20 20" />,
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 13h4" />
    </>
  ),
  // Two arrows chasing each other: the conventional glyph for "this recurs",
  // used for repeating tasks and events alike.
  repeat: (
    <>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </>
  ),
  // An arrow winding back round a clock: things you can bring back.
  restore: (
    <>
      <path d="M3 12a9 9 0 1 0 2.6-6.4L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  at: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M16 12v1.5a2.5 2.5 0 0 0 5 0V12a9 9 0 1 0-5.5 8.3" />
    </>
  ),
  spinner: <path d="M12 3a9 9 0 1 0 9 9" />,
};

export type IconProps = {
  name: IconName;
  size?: number;
  strokeWidth?: number;
} & Omit<SVGProps<SVGSVGElement>, "name">;

export function Icon({ name, size = 18, strokeWidth = 1.8, ...rest }: IconProps) {
  const isDots = name === "more";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={isDots ? "currentColor" : "none"}
      stroke={isDots ? "none" : "currentColor"}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={name === "spinner" ? "ui-spin" : undefined}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

export const iconNames = Object.keys(PATHS) as IconName[];
